import React from "react";
import { View, Text, StyleSheet, TouchableOpacity, Image } from "react-native";
import { FontAwesome } from "@expo/vector-icons";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useSelector } from "react-redux";
import { Avatar } from "react-native-paper";
import * as Okta from "../../services/oktaAuthService";
import oktaConfig from "../../configs/okta";
import { clearSessionStorage } from "../../services/utils/storageService";
import { persistor } from "../../redux/configureStore";

export default function MobileSettingsScreen({ navigation }: any) {
  const { userInfo, profilePhoto, isDrawer, hasAvatar, currentRole, logo } =
    useSelector((state: any) => state.configurations);

  const displayName =
    userInfo?.display_name && userInfo.display_name !== "undefined"
      ? userInfo.display_name
      : null;
  const fullName: string[] = displayName?.split(" ") ?? [];
  const initial = fullName.length > 0 ? fullName[0].charAt(0) : "U";

  const { loading, notificationCount } = useSelector(
    (state: any) => state.notification
  );

  const logoutHandler = () => {
    Okta.deleteSession().then(() => {
      clearSessionStorage();
      window.location.href = `${oktaConfig.api.redirectUri}`;
    });
    persistor.purge();
  };

  const isAdmin = ["OWNER", "ADMIN"].includes(currentRole);

  const menuItems = [
    {
      key: "account",
      icon: <FontAwesome name="user" size={18} color="#6B7280" />,
      label: "Account Settings",
      subtitle: "Profile, password & preferences",
      onPress: () =>
        navigation.navigate("UserSettings", { screen: "Profile" }),
      show: true,
    },
    {
      key: "users",
      icon: <FontAwesome name="users" size={16} color="#6B7280" />,
      label: "User Management",
      subtitle: "Manage team members & roles",
      onPress: () =>
        navigation.navigate("OrgSettings", { screen: "Users" }),
      show: isAdmin,
    },
    {
      key: "org",
      icon: <FontAwesome name="building" size={16} color="#6B7280" />,
      label: "Organisation Settings",
      subtitle: "Company details & configuration",
      onPress: () =>
        navigation.navigate("OrgSettings", { screen: "Company" }),
      show: isAdmin,
    },
    {
      key: "notifications",
      icon: <MaterialCommunityIcons name="bell-alert" size={20} color="#6B7280" />,
      label: "Notifications",
      subtitle: "Alerts, reminders & updates",
      onPress: () =>
        navigation.navigate("UserSettings", { screen: "Notification" }),
      show: true,
      badge: !loading && notificationCount > 0 ? notificationCount : null,
    },
  ];

  return (
    <View style={styles.container}>
      <View style={{ flex: 1 }}>
        {/* Company Logo */}
        {logo && (
          <View style={styles.logoContainer}>
            <Image
              source={logo}
              style={styles.logo}
              resizeMode="contain"
            />
          </View>
        )}

        {/* User Profile Card */}
        <View style={styles.profileCard}>
          <View style={styles.avatarContainer}>
            {hasAvatar ? (
              <Avatar.Image
                size={48}
                source={profilePhoto}
                style={{ backgroundColor: "white" }}
              />
            ) : (
              <Avatar.Text
                size={48}
                label={initial}
                style={styles.avatarText}
              />
            )}
          </View>
          <View style={styles.profileInfo}>
            <Text style={styles.profileName}>
              {displayName || "User"}
            </Text>
            <Text style={styles.profileRole}>
              {currentRole ? currentRole.charAt(0) + currentRole.slice(1).toLowerCase() : "Member"}
            </Text>
          </View>
        </View>

        {/* Menu Items */}
        <View style={styles.menuSection}>
          {menuItems
            .filter((item) => item.show)
            .map((item) => (
              <TouchableOpacity
                key={item.key}
                style={styles.menuItem}
                onPress={item.onPress}
                activeOpacity={0.6}
              >
                <View style={styles.menuIconContainer}>
                  {item.icon}
                  {item.badge && (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>{item.badge}</Text>
                    </View>
                  )}
                </View>
                <View style={styles.menuTextContainer}>
                  <Text style={styles.menuLabel}>{item.label}</Text>
                  <Text style={styles.menuSubtitle}>{item.subtitle}</Text>
                </View>
                <FontAwesome name="chevron-right" size={11} color="#D1D5DB" />
              </TouchableOpacity>
            ))}
        </View>
      </View>

      {/* Sign Out */}
      <View style={styles.logoutSection}>
        <TouchableOpacity
          style={styles.logoutButton}
          onPress={logoutHandler}
          activeOpacity={0.6}
        >
          <MaterialCommunityIcons name="logout" size={18} color="#EF4444" />
          <Text style={styles.logoutText}>Sign Out</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },

  /* Company Logo */
  logoContainer: {
    alignItems: "center",
    marginTop: 14,
    marginBottom: 4,
  },
  logo: {
    width: 110,
    height: 30,
  },

  /* Profile Card */
  profileCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    marginHorizontal: 14,
    marginTop: 14,
    marginBottom: 8,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  avatarContainer: {
    marginRight: 12,
  },
  avatarText: {
    backgroundColor: "#2563EB",
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 16,
    fontWeight: "700",
    color: "#111827",
  },
  profileRole: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 2,
  },

  /* Menu Section */
  menuSection: {
    backgroundColor: "#fff",
    marginHorizontal: 14,
    marginTop: 6,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
    overflow: "hidden",
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  menuIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  menuTextContainer: {
    flex: 1,
  },
  menuLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
  },
  menuSubtitle: {
    fontSize: 12,
    color: "#9CA3AF",
    marginTop: 2,
  },

  /* Badge */
  badge: {
    position: "absolute",
    top: -4,
    right: -4,
    backgroundColor: "#EF4444",
    borderRadius: 10,
    paddingHorizontal: 5,
    minWidth: 18,
    height: 18,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#fff",
  },
  badgeText: {
    color: "#fff",
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
  },

  /* Logout */
  logoutSection: {
    paddingHorizontal: 14,
    paddingBottom: 24,
  },
  logoutButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 13,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#FEE2E2",
    backgroundColor: "#FEF2F2",
  },
  logoutText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#EF4444",
    marginLeft: 8,
  },
});
